import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import { IconButton } from "@mui/material";
import { IoEye, IoEyeOff } from "react-icons/io5";
import { ReactComponent as Grb } from "../../assets/svg/srbija-grb.svg";
import { resetPassword } from "../../server/server";
import { validatePasswordComplexity } from "../../common/PasswordValidationHelper";

const theme = createTheme({
  typography: { fontFamily: "Fira Sans Condensed" },
});

const NewPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});
  const [fieldTouched, setFieldTouched] = useState<{
    password?: boolean;
    confirmPassword?: boolean;
  }>({});

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!token || !email) {
      setErrorMessage("Неважећи линк за ресетовање лозинке");
    }
  }, [token, email]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const password = data.get("password") as string;
    const confirmPassword = data.get("confirmPassword") as string;
    const errors: { password?: string; confirmPassword?: string } = {};

    if (!password) {
      errors.password = "Обавезно поље";
    } else {
      const passwordError = validatePasswordComplexity(password);
      if (passwordError) {
        errors.password = passwordError;
      }
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Обавезно поље";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Лозинке се не поклапају";
    }

    setFieldTouched({ password: true, confirmPassword: true });
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setErrorMessage("");
      setSuccessMessage("");
      return;
    }

    const resp = await resetPassword({
      token: token!,
      email: email!,
      password,
    });
    if (resp?.status === 201) {
      setSuccessMessage("Лозинка је успешно промењена.");
      setErrorMessage("");
      setTimeout(() => navigate("/login"), 2000);
    } else {
      setErrorMessage(resp?.data?.message || "Грешка при промени лозинке");
      setSuccessMessage("");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <div>
            <div className="flex flex-col justify-between gap-2 ml-3 items-center">
              <Grb />
              <div className="flex flex-col mt-2 items-center">
                <span className="font-medium text-xl">Република Србија</span>
                <span className="text-xl">
                  Министарство за европске интеграције
                </span>
              </div>
            </div>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Нова лозинка"
                type={showPassword ? "text" : "password"}
                id="password"
                autoFocus
                error={Boolean(fieldErrors.password)}
                helperText={fieldErrors.password}
                onBlur={() =>
                  setFieldTouched((prev) => ({ ...prev, password: true }))
                }
                FormHelperTextProps={{ style: { color: "red" } }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      {showPassword ? <IoEyeOff /> : <IoEye />}
                    </IconButton>
                  ),
                  style: fieldErrors.password
                    ? { borderColor: "red" }
                    : undefined,
                }}
                inputProps={{
                  style: fieldErrors.password ? { color: "red" } : undefined,
                  placeholder: fieldErrors.password
                    ? "Обавезно поље"
                    : undefined,
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Потврда лозинке"
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                error={Boolean(fieldErrors.confirmPassword)}
                helperText={fieldErrors.confirmPassword}
                onBlur={() =>
                  setFieldTouched((prev) => ({
                    ...prev,
                    confirmPassword: true,
                  }))
                }
                FormHelperTextProps={{ style: { color: "red" } }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      {showConfirmPassword ? <IoEyeOff /> : <IoEye />}
                    </IconButton>
                  ),
                  style: fieldErrors.confirmPassword
                    ? { borderColor: "red" }
                    : undefined,
                }}
                inputProps={{
                  style: fieldErrors.confirmPassword
                    ? { color: "red" }
                    : undefined,
                  placeholder: fieldErrors.confirmPassword
                    ? "Обавезно поље"
                    : undefined,
                }}
              />
              {errorMessage && (
                <Typography component="p" sx={{ color: "error.main" }}>
                  {errorMessage}
                </Typography>
              )}
              {successMessage && (
                <Typography component="p" sx={{ color: "success.main" }}>
                  {successMessage}
                </Typography>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, backgroundColor: "#0F2765" }}
              >
                Промени лозинку
              </Button>
              <div
                className="text-center mt-1 text-[18px] cursor-pointer text-darkBlue underline"
                onClick={() => navigate("/login")}
              >
                Назад на пријаву
              </div>
            </Box>
          </div>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default NewPassword;
